/** Consented, minimized booking and enquiry events. No pageviews or public URL markers. */
(function (global) {
  'use strict';
  if (global.KieuBookingGA4) return;
  const transports = new WeakMap();
  const LAYER = 'kieuGa4DataLayer';
  const STEPS = new Set(['step_1', 'step_2', 'step_3', 'step_4', 'step_5', 'step_6', 'step_7']);
  const ACTIONS = {
    select_location: 'option_selected', select_service: 'option_selected',
    select_addon: 'option_selected', skip_addons: 'optional_step_skipped',
    select_period: 'filter_changed', select_doctor: 'filter_changed', clear_doctor: 'filter_cleared',
    expand_more_dates: 'more_options_opened', load_more_weeks: 'more_options_loaded',
    prev_calendar_week: 'previous_options', next_calendar_week: 'next_options',
    select_appointment: 'option_selected', submit_contact_form: 'form_submitted',
    contact_validation_failed: 'validation_failed', submit_otp: 'verification_submitted',
    resend_otp: 'verification_resent', phone_verified_late: 'verification_completed',
    retry_locations: 'retry', retry_services: 'retry', retry_slots: 'retry',
    change_service: 'edit_opened', change_datetime: 'edit_opened', change_phone: 'edit_opened',
    add_calendar: 'calendar_opened', back: 'back_clicked', stepper_back: 'stepper_clicked',
  };
  const ERRORS = new Set(['load_locations', 'load_services', 'load_slots', 'slot_unavailable', 'booking_failed', 'otp_failed', 'iframe_load_failed']);
  const REASONS = new Set(['close_button', 'modal_close', 'backdrop', 'escape', 'pagehide', 'new_booking', 'unknown']);
  const DIRECTIONS = new Set(['forward', 'back', 'jump', 'edit', 'auto', 'system']);
  const ENTRY_POINTS = new Set([
    'header_desktop', 'header_mobile', 'hero_primary', 'booking_banner',
    'sticky_booking', 'reimbursement_result', 'legacy_booking_link',
    'booking_label_match', 'booking_page_auto', 'unknown',
  ]);
  const attempt = (fn, fallback) => { try { return fn(); } catch { return fallback; } };
  const step = (value) => { const key = `step_${Number(value)}`; return STEPS.has(key) ? key : 'unknown'; };

  function allowed(win) {
    return attempt(() => {
      const privacy = win.Shopify?.customerPrivacy;
      // A permissive regional default is not an explicit analytics choice.
      return privacy?.analyticsProcessingAllowed?.() === true
        && privacy?.currentVisitorConsent?.().analytics === 'yes';
    }, false);
  }

  function transportFor(win, doc, id) {
    if (!transports.has(win)) transports.set(win, new Map());
    const byId = transports.get(win);
    if (byId.has(id)) return byId.get(id);
    let ready = false, loading = false, configured = false, consent = false, disabledByUs = false;
    let pending = [], loadAttempts = 0;
    const disableKey = `ga-disable-${id}`;
    const origin = attempt(() => {
      const url = new URL(win.location.origin);
      return /^https?:$/.test(url.protocol) ? url.origin : null;
    }, null);
    const fields = {
      page_location: `${origin}/`,
      page_title: 'Booking', page_referrer: '',
      allow_google_signals: false, allow_ad_personalization_signals: false,
    };
    function push(args) {
      const layer = win[LAYER] = win[LAYER] || [];
      // gtag uses an Arguments object; a plain array is a different dataLayer command.
      (function () { layer.push(arguments); }).apply(null, args);
    }
    const consentState = (value) => ({ analytics_storage: value ? 'granted' : 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    function refresh() {
      const next = allowed(win) && !!origin && !(win[disableKey] === true && !disabledByUs);
      if (!next) {
        pending = [];
        if (consent && ready) {
          win[disableKey] = true;
          disabledByUs = true;
          push(['consent', 'update', consentState(false)]);
        }
      } else if (!consent && ready && configured) {
        if (disabledByUs) { win[disableKey] = false; disabledByUs = false; }
        push(['consent', 'update', consentState(true)]);
      }
      consent = next;
      return next;
    }
    function initialize() {
      if (configured || !ready || !refresh()) return;
      if (disabledByUs) { win[disableKey] = false; disabledByUs = false; }
      push(['consent', 'default', consentState(false)]);
      push(['consent', 'update', consentState(true)]);
      push(['js', new Date()]);
      push(['config', id, { ...fields, send_page_view: false }]);
      configured = true;
    }
    function flush() {
      if (!ready || !refresh()) return;
      initialize();
      while (pending.length && refresh()) push(pending.shift());
    }
    function load() {
      if (ready || loading || loadAttempts >= 2 || !refresh()) return;
      loading = true; loadAttempts += 1;
      win[LAYER] = win[LAYER] || [];
      const script = doc.createElement('script');
      script.async = true; script.referrerPolicy = 'no-referrer';
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}&l=${LAYER}`;
      script.onload = () => attempt(() => { ready = true; loading = false; flush(); });
      script.onerror = () => { loading = false; pending = []; };
      (doc.head || doc.documentElement).appendChild(script);
    }
    const api = {
      refresh: () => attempt(refresh, false),
      prepare() {
        if (!refresh()) return false;
        if (ready) initialize(); else load();
        return true;
      },
      send(name, params) {
        return attempt(() => {
          if (!refresh()) return false;
          const command = ['event', name, { ...fields, ...params, send_to: id }];
          if (ready) { initialize(); push(command); return true; }
          if (pending.length >= 100) return false;
          pending.push(command); load(); return true;
        }, false);
      },
    };
    doc?.addEventListener?.('visitorConsentCollected', () => { api.refresh(); attempt(flush); });
    doc?.addEventListener?.('visibilitychange', () => api.refresh());
    win?.addEventListener?.('pageshow', () => api.refresh());
    byId.set(id, api);
    return api;
  }

  function createAdapter({ measurementId, windowRef = global, documentRef = global.document } = {}) {
    const id = /^G-[A-Z0-9]{4,20}$/i.test(String(measurementId || '')) ? String(measurementId).toUpperCase() : '';
    const transport = id ? transportFor(windowRef, documentRef, id) : null;
    let destroyed = false, opened = false, started = false, succeeded = false, abandoned = false;
    let lastStep = 'unknown', lastSentStep = null, surface = 'modal', entryPoint = 'unknown';
    function emit(name, params = {}) {
      if (destroyed) return false;
      return transport?.send(`kieu_ga4_booking_${name}`, {
        booking_surface: surface,
        booking_entry_point: entryPoint,
        booking_step: lastStep,
        ...params,
      }) === true;
    }
    function abandon(reason) {
      if (succeeded || abandoned) return;
      abandoned = true;
      emit('abandon', { booking_close_reason: REASONS.has(reason) ? reason : 'unknown' });
    }
    function open(options = {}) {
      opened = true; started = false; succeeded = false; abandoned = false;
      lastStep = 'unknown'; lastSentStep = null;
      surface = options.surface === 'full_page' ? 'full_page' : 'modal';
      entryPoint = ENTRY_POINTS.has(options.entryPoint) ? options.entryPoint : 'unknown';
      if (options.clicked === true) emit('click');
      emit('open');
    }
    const adapter = {
      open,
      tracking(payload) {
        if (!opened || !payload || typeof payload !== 'object') return;
        switch (payload.event) {
          case 'kieu_booking_start':
            if (!started) started = emit('start');
            break;
          case 'kieu_booking_step_view':
            lastStep = step(payload.booking_step_index);
            if (lastStep === 'unknown' || lastStep === lastSentStep) break;
            if (emit('step_view')) lastSentStep = lastStep;
            break;
          case 'kieu_booking_step_transition':
            emit('step_change', { booking_step: step(payload.booking_step_index), booking_next_step: step(payload.booking_next_step_index), booking_direction: DIRECTIONS.has(payload.booking_transition_reason) ? payload.booking_transition_reason : 'unknown' });
            break;
          case 'kieu_booking_action': {
            if (payload.booking_action === 'new_booking') { open({ surface, entryPoint }); break; }
            const action = Object.hasOwn(ACTIONS, payload.booking_action) ? ACTIONS[payload.booking_action] : null;
            if (action) emit('action', { booking_action: payload.auto === true ? 'automatic_selection' : action });
            break;
          }
          case 'kieu_booking_error':
            if (ERRORS.has(payload.booking_error_type)) emit('error', { booking_error: payload.booking_error_type });
            break;
          case 'kieu_booking_abandon':
            lastStep = step(payload.booking_step_index);
            abandon(payload.booking_abandon_reason);
            break;
          // A confirmation view/legacy complete event is never a success source.
        }
      },
      confirmed() {
        if (!opened || succeeded) return;
        succeeded = true; abandoned = false;
        emit('success', { booking_step: 'step_7' });
      },
      close(reason = 'unknown') {
        if (!opened) return;
        const safeReason = REASONS.has(reason) ? reason : 'unknown';
        emit('close', { booking_step: succeeded ? 'step_7' : lastStep, booking_close_reason: safeReason });
        abandon(safeReason); opened = false;
      },
      refreshConsent() { return transport?.refresh() === true; },
      destroy() { destroyed = true; },
    };
    return Object.fromEntries(Object.entries(adapter).map(([key, fn]) => [key, (...args) => attempt(() => fn(...args))]));
  }
  function leadTransport({ measurementId, windowRef = global, documentRef = global.document } = {}) {
    const id = /^G-[A-Z0-9]{4,20}$/i.test(String(measurementId || '')) ? String(measurementId).toUpperCase() : '';
    return id ? transportFor(windowRef, documentRef, id) : null;
  }
  function prepareLead(options) {
    return leadTransport(options)?.prepare() === true;
  }
  function sendLead(options) {
    const transport = leadTransport(options);
    if (!transport?.refresh()) return Promise.resolve(false);
    return new Promise((resolve) => {
      let finished = false;
      const finish = (processed) => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        resolve(processed);
      };
      const timeout = setTimeout(() => finish(false), 1200);
      if (!transport.send('generate_lead', {
        page_title: 'Enquiry',
        event_callback: () => finish(true),
        event_timeout: 1200,
      })) finish(false);
    });
  }
  function sendContact(options, method) {
    if (method !== 'phone' && method !== 'email') return false;
    return leadTransport(options)?.send('kieu_contact_click', {
      page_title: 'Contact',
      contact_method: method,
    }) === true;
  }
  global.KieuBookingGA4 = Object.freeze({ createAdapter, prepareLead, sendLead, sendContact });
})(typeof window !== 'undefined' ? window : globalThis);
